import type { LocaleObject } from '@nuxtjs/i18n'

interface AlternateLink {
  rel: string
  hreflang: string
  href: string
}

export const useAlternateLinks = () => {
  const { locales } = useI18n()
  const route = useRoute()
  const config = useRuntimeConfig()
  const baseUrl = config.public.siteUrl as string

  /**
   * Generate alternate links for all available locales
   * Follows Google's guidelines for multilingual content
   * https://developers.google.com/search/docs/specialty/international/localized-versions
   */
  const getAlternateLinks = (): AlternateLink[] => {
    const links: AlternateLink[] = []
    // Fully decode the path to handle double-encoded URLs (e.g. %253F → %3F → ?)
    let decoded = route.path
    try {
      let prev = ''
      while (prev !== decoded) {
        prev = decoded
        decoded = decodeURIComponent(decoded)
      }
    } catch {
      // If decoding fails, use whatever we have
    }
    const currentPath = decoded.includes('?') ? decoded.split('?')[0] : decoded

    // Get all locales as LocaleObject array
    const allLocales = locales.value as LocaleObject[]

    // Helper to normalize path by removing trailing slash (except for root)
    const normalizeTrailingSlash = (path: string): string => {
      if (path === '/') return path
      return path.endsWith('/') ? path.slice(0, -1) : path
    }

    // Generate alternate link for each locale
    allLocales.forEach((loc) => {
      const localeCode = loc.code
      const isoCode = loc.iso || localeCode

      let localizedPath: string

      // Handle default locale (ru) - no prefix
      if (localeCode === 'ru') {
        // Remove language prefix if present for default locale
        localizedPath = currentPath.replace(/^\/ky/, '')
        // Ensure path starts with /
        if (!localizedPath.startsWith('/')) {
          localizedPath = '/' + localizedPath
        }
      } else {
        // For non-default locales (ky), add prefix
        if (currentPath.startsWith(`/${localeCode}`)) {
          // Already has the correct prefix
          localizedPath = currentPath
        } else if (currentPath.startsWith('/ky') || currentPath.startsWith('/ru')) {
          // Has another locale prefix, replace it
          localizedPath = currentPath.replace(/^\/[a-z]{2}/, `/${localeCode}`)
        } else {
          // No prefix, add it
          localizedPath = `/${localeCode}${currentPath}`
        }
      }

      // Normalize trailing slash
      localizedPath = normalizeTrailingSlash(localizedPath)

      links.push({
        rel: 'alternate',
        hreflang: isoCode,
        href: baseUrl + encodeURI(localizedPath)
      })
    })

    // Add x-default pointing to the default locale (Russian)
    const defaultPath = normalizeTrailingSlash(currentPath.replace(/^\/ky/, ''))
    links.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: baseUrl + encodeURI(defaultPath.startsWith('/') ? defaultPath : '/' + defaultPath)
    })

    return links
  }

  return {
    getAlternateLinks
  }
}
